import '@saleor/macaw-ui-next/style';
import './index.css';

import { ApolloProvider } from '@apollo/client';
import { history, Router } from '@dashboard/components/Router';
import { SaleorProvider } from '@dashboard/legacy-sdk';
import { ThemeProvider } from '@dashboard/theme';
import { ThemeProvider as LegacyThemeProvider } from '@saleor/macaw-ui';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { useIntl } from 'react-intl';

import AuthProvider from './auth/AuthProvider';
import LoginLoading from './auth/components/LoginLoading/LoginLoading';
import { useAuthRedirection } from './auth/hooks/useAuthRedirection';
import { LocaleProvider } from './components/Locale';
import { NotificationProvider } from './components/notifications';
import { ErrorHistoryProvider } from './components/notifications/ErrorHistoryContext';
import { WindowTitle } from './components/WindowTitle';
import { GTM_ID } from './config';
import { apolloClient, saleorClient } from './graphql/client';
import { commonMessages } from './intl';
import { errorTracker } from './services/errorTracking';
import { paletteOverrides, themeOverrides } from './themeOverrides';

const Auth = lazy(() => import('./auth'));
const AuthenticatedApp = lazy(() => import('./AuthenticatedApp'));

// `react-gtm-module` is ~30 KB ungzipped and only useful when GTM_ID is
// configured at deploy time. Loading it dynamically keeps the entry chunk
// slim for self-hosted deployments that never set GTM_ID.
if (GTM_ID) {
  void import('react-gtm-module').then(({ default: TagManager }) => {
    TagManager.initialize({ gtmId: GTM_ID });
  });
}

errorTracker.init(history);

/*
  Handle legacy theming toggle. Since we use new and old macaw,
  we need to handle both theme swticher for a while.
*/
const handleLegacyTheming = (): void => {
  const activeTheme = localStorage.getItem('activeMacawUITheme');

  if (activeTheme === 'defaultDark') {
    localStorage.setItem('macaw-ui-theme', 'dark');

    return;
  }

  localStorage.setItem('macaw-ui-theme', 'light');
};

handleLegacyTheming();

const App = (): JSX.Element => (
  <SaleorProvider client={saleorClient}>
    <ApolloProvider client={apolloClient}>
      <Router>
        {/* @ts-expect-error legacy types */}
        <LegacyThemeProvider overrides={themeOverrides} palettes={paletteOverrides}>
          <ThemeProvider>
            <LocaleProvider>
              <NotificationProvider>
                <ErrorHistoryProvider>
                  <AuthProvider>
                    <Routes />
                  </AuthProvider>
                </ErrorHistoryProvider>
              </NotificationProvider>
            </LocaleProvider>
          </ThemeProvider>
        </LegacyThemeProvider>
      </Router>
    </ApolloProvider>
  </SaleorProvider>
);
const Routes = () => {
  const intl = useIntl();
  const { authenticated, authenticating } = useAuthRedirection();

  return (
    <>
      <WindowTitle title={intl.formatMessage(commonMessages.dashboard)} />
      {authenticated ? (
        <Suspense fallback={<LoginLoading />}>
          <AuthenticatedApp />
        </Suspense>
      ) : authenticating ? (
        <LoginLoading />
      ) : (
        <Suspense fallback={<LoginLoading />}>
          <Auth />
        </Suspense>
      )}
    </>
  );
};

const root = createRoot(document.querySelector('#dashboard-app')!);

// StrictMode is development-only (no effect in production)
// Set VITE_DISABLE_STRICT_MODE=true to disable for testing
const enableStrictMode = import.meta.env.DEV && import.meta.env.VITE_DISABLE_STRICT_MODE !== 'true';

root.render(
  enableStrictMode ? (
    <StrictMode>
      <App />
    </StrictMode>
  ) : (
    <App />
  ),
);
